import { Component, Prop } from 'vue-property-decorator';
import TombolaBaseComponent from '@/components/base/base.component';
import { SearchResult } from '@/components/home/_components/search-bar/_domain/models/search-result-model';
import { IContextMenuOption } from '@/app-modules/tombola-context-menu';
import { IChatServerService } from '@/app-modules/server-service';
import { ISinBanService } from '@/components/chat/_domain/modules/sin-ban-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IChatPrizeService } from '@/components/chat/_domain/modules/chat-prize-service';
import { User } from '@/app-modules/store/modules/user';
import { UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { ISafeplayService } from '@/components/chat/_domain/modules/safeplay-service';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { ConfigurationPaths } from '@/app-modules/config';
import { IUserNominationService } from '@/components/chat/_domain/modules/user-nomination-service';
import { ChatContextInteractionType, ChatContextInteractionSource } from '@/components/chat/_domain/modules/chat-context-menu';
import _ from 'lodash';

@Component({
  name: 'SearchResultsItemComponent'
})
export default class SearchResultsItemComponent extends TombolaBaseComponent {
  @Prop() public result: SearchResult;
  @Prop() public server: IChatServerService;
  private menuOptions: IContextMenuOption[] = [];
  private isPlayerNotesLiveFeatureToggleEnabled: boolean = false;
  private isUsePlayerNotesBetaLiveFeatureToggleEnabled: boolean = false;
  private isUserNominationEnabled: boolean = false;
  private isModalsAsChatContextInteractionEnabled: boolean = false;
  private playerNotesBetaList: string[] = [];

  protected created() {
    this.initialisePlayerNotesFeatureToggleStateAndMenuOptions();
  }

  private initialiseMenuOptions() {
    const menuOptionsArray: IContextMenuOption[] = [
      this.getSpawnChatRoomMenuOption(),
      this.getAwardChatPrizeMenuOption(),
      this.getSinBinUserMenuOption(),
      this.getBanUserMenuOption(),
      this.getSafeplayReferralMenuOption()
    ];
    if (this.shouldShowPlayerNotes()) {
      menuOptionsArray.push(this.getPlayerNotesMenuOption());
    }
    if (this.isUserNominationEnabled) {
      menuOptionsArray.push(this.getUserNominationMenuOption());
    }
    this.menuOptions.push(...menuOptionsArray);
  }

  private shouldShowPlayerNotes(){
    if (!this.isPlayerNotesLiveFeatureToggleEnabled){
      return false;
    }

    if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
      const user = this.$tombolaStore.Get(User);
      return this.playerNotesBetaList.includes(user.username);
    }

    return true;
  }

  private isCurrentUserTeamLeader(): boolean {
    const user = this.$tombolaStore.Get(User);
    return user.userChatStatus === UserChatStatus.TeamLeader;
  }

  private isCurrentUserShiftLeader(): boolean {
    const user = this.$tombolaStore.Get(User);
    return user.userChatStatus === UserChatStatus.ShiftLeader;
  }

  private initialisePlayerNotesFeatureToggleStateAndMenuOptions() {
    this.configService.getConfiguration().then((config) => {
      this.isPlayerNotesLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.player_notes_islive) === 'true';
      this.isUsePlayerNotesBetaLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_player_notes_beta_islive) === 'true';
      this.isUserNominationEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.user_nomination_islive) === 'true';
      this.isModalsAsChatContextInteractionEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.chatcontext_modals_islive) === 'true';
      if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
        const list = config.getConfigurationByPath(ConfigurationPaths.users.playernotes_beta);
        if (list !== undefined){
          this.playerNotesBetaList = list.split(',');
        }
      }
      this.initialiseMenuOptions();
    });
  }

  private getSpawnChatRoomMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.app.workbench.search.gotoChatRoom,
      iconCssClass: 'fa-comments',
      menuOptionSelectedCallback: () => this.spawnChatRoom()
    };
  }

  private getSinBinUserMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.sin,
      iconCssClass: 'fa-trash-o',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.SinPlayer, this.result.username, this.handleChatContextInteraction.bind(this), this.result.roomName)
      : this.sinBinUser()
    };
  }

  private getBanUserMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.ban,
      iconCssClass: 'fa-ban',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.BanPlayer, this.result.username, this.handleChatContextInteraction.bind(this), this.result.roomName)
      : this.banUser()
    };
  }

  private getAwardChatPrizeMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.pay,
      iconCssClass: 'fa-gbp',
      menuOptionSelectedCallback: () => this.awardChatPrize()
    };
  }

  private getSafeplayReferralMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.safeplayReferral,
      iconCssClass: 'fa-exclamation-triangle',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.SafePlayReferral, this.result.username, this.handleChatContextInteraction.bind(this))
      : this.referUserToSafeplay()
    };
  }

  private getUserNominationMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.userNomination,
      iconCssClass: 'fa-star',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.CommunityNomination, this.result.username, this.handleChatContextInteraction.bind(this))
      : this.userNominationReferral()
    };
  }

  private getPlayerNotesMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.playerNotes,
      iconCssClass: 'fa-sticky-note',
      menuOptionSelectedCallback: () => this.spawnPlayerNotesModal()
    };
  }

  private spawnChatRoom() {
    if (this.result) {
      this.$eventbus.$emit('spawnChatModal', this.result.roomName);
    }
  }

  private sinBinUser(): void {
    if (this.result) {
      this.sinBanService.sinBinUser(this.result.username, this.server, this.result.roomName);
    }
  }

  private banUser(): void {
    if (this.result) {
      this.sinBanService.banUser(this.result.username, this.server, this.result.roomName);
    }
  }

  private awardChatPrize(): void {
    if (this.result) {
      const username = this.result.username;
      this.chatPrizeService.awardPlayerChatPrize([username], null, this.server);
    }
  }

  private referUserToSafeplay(): void {
    if (this.result) {
      const username = this.result.username;
      this.safeplayService.referUser(username);
    }
  }

  private userNominationReferral(): void {
    if (this.result) {
      const username = this.result.username;
      this.userNominationService.referUser(this.result.username);
    }
  }

  private spawnPlayerNotesModal(): void {
    if (this.result) {
      const username = this.result.username;
      this.savePlayerNotesUsernameToStore(username);
      this.eventBusService.getEventBus().$emit('spawnPlayerNotesModal');
    }
  }

  private savePlayerNotesUsernameToStore(username: string): void {
    const userPreferences = this.persistedStoreService.Get(UserPreferences);
    userPreferences.playerNotesUsername = username;
    this.persistedStoreService.Save(userPreferences);
    this.emitUserSettingsUpdatedEvent();
  }

  private spawnChatContextInteractionModal(chatContextAction: ChatContextInteractionType, playerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, originalChatRoomName?: string): void {
    this.eventBusService.getEventBus().$emit('spawnChatContextInteractionModal', chatContextAction, playerUsername, handleChatContextInteractionCallback, originalChatRoomName, ChatContextInteractionSource.SearchResultItem);
  }

  private handleChatContextInteraction(chatContextAction: ChatContextInteractionType, actionReason?: string, chatRoom?: string): void {
    switch (chatContextAction) {
      case ChatContextInteractionType.BanPlayer:
        this.sinBanService.banUser(this.result.username, this.server, chatRoom || this.result.roomName, actionReason);
        break;
      case ChatContextInteractionType.SinPlayer:
        this.sinBanService.sinBinUser(this.result.username, this.server, chatRoom || this.result.roomName, actionReason);
        break;
      case ChatContextInteractionType.SafePlayReferral:
        this.safeplayService.referUser(this.result.username, actionReason);
        break;
      case ChatContextInteractionType.CommunityNomination:
        this.userNominationService.referUser(this.result.username, actionReason);
      default:
        break;
    }
  }

  private emitUserSettingsUpdatedEvent(): void {
    this.eventBusService.getEventBus().$emit(UserSettingsUpdatedEventName);
  }

  private get sinBanService(): ISinBanService {
    return this.$container.get(SERVICE_IDENTIFIERS.ISinBanService);
  }

  private get chatPrizeService(): IChatPrizeService {
    return this.$container.get(SERVICE_IDENTIFIERS.IChatPrizeService);
  }

  private get safeplayService(): ISafeplayService {
    return this.$container.get(SERVICE_IDENTIFIERS.ISafeplayService);
  }

  private get userNominationService(): IUserNominationService {
    return this.$container.get(SERVICE_IDENTIFIERS.IUserNominationService);
  }

  private get persistedStoreService(): IPersistedStore {
    return this.$container.get(SERVICE_IDENTIFIERS.IPersistedStore);
  }

  private get eventBusService(): IEventBusService {
    return this.$container.get(SERVICE_IDENTIFIERS.IEventBusService);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/home/_components/search-bar/_components/search-results-list/_components/search-results-item/search-results-item.component.ts